import { createWorker } from 'tesseract.js';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const cleanExtractedText = (text) => (
  text
    .replace(/\n\s*\n\s*\n/g, '\n\n')
    .replace(/[ \t]+/g, ' ')
    .trim()
);

const extractPdfText = async (file) => {
  try {
    const pdf = await pdfjsLib.getDocument(await file.arrayBuffer()).promise;
    const pages = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      pages.push(content.items.map((item) => item.str).join(' '));
    }

    const text = cleanExtractedText(pages.join('\n\n'));

    if (text.length < 50) {
      throw new Error(
        'This PDF contains little selectable text. Try uploading a clear image of the report instead.'
      );
    }

    return text;
  } catch (error) {
    if (error.message.includes('little selectable text')) {
      throw error;
    }

    throw new Error(
      'The PDF could not be read. It may be corrupted, password-protected, or image-based.'
    );
  }
};

const extractImageText = async (file, onProgress) => {
  let worker;

  try {
    worker = await createWorker('eng', 1, {
      logger: (event) => {
        if (event.status === 'recognizing text') {
          onProgress?.(Math.round((event.progress || 0) * 100));
        }
      }
    });

    const result = await worker.recognize(file);
    const text = cleanExtractedText(result.data.text);

    if (text.length < 20) {
      throw new Error(
        'Not enough readable text was found. Upload a sharper, well-lit image without glare.'
      );
    }

    return text;
  } finally {
    if (worker) {
      await worker.terminate();
    }
  }
};

export const validateMedicalFile = (file) => {
  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'text/plain'
  ];

  if (!allowedTypes.includes(file.type)) {
    throw new Error('Unsupported file type. Use PDF, JPEG, PNG, or TXT.');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('The selected file is larger than the 10 MB limit.');
  }
};

export const extractDocumentText = async (file, onProgress) => {
  validateMedicalFile(file);

  if (file.type === 'text/plain') {
    return cleanExtractedText(await file.text());
  }

  if (file.type === 'application/pdf') {
    return extractPdfText(file);
  }

  return extractImageText(file, onProgress);
};